import * as THREE from 'three';
import type { CapeId, CharacterRig } from './types';
import { createCapeShader } from '../shaders/cape-shader';
import { applyCapeParams } from './cape-shader-params';

const CAPE_COLLAR_WIDTH = 0.64;
const CAPE_SHOULDER_WIDTH = 0.7;
const CAPE_BOTTOM_WIDTH = 1.08;
const CAPE_HEIGHT = 0.96;
const CAPE_WIDTH_SEGMENTS = 6;
const CAPE_HEIGHT_SEGMENTS = 5;
const CAPE_COLLAR_BACK_OFFSET = 0.08;
const CAPE_COLLAR_SIDE_FORWARD = 0.035;
const CAPE_COLLAR_SIDE_DROP = 0.035;
const CAPE_SHOULDER_BACK_DELTA = 0.28;
const CAPE_BOTTOM_BACK_DELTA = 0.44;
const CAPE_PIN_DROP = 0.015;
const CAPE_BACK_SURFACE_GAP = 0.035;
const CAPE_SHOULDER_SURFACE_GAP = 0.02;
const CAPE_SHOULDER_SURFACE_STRENGTH = 0.22;
const CAPE_GRAVITY = 2.6;
const CAPE_DAMPING = 0.985;
const CAPE_TELEPORT_RESET_DISTANCE = 1.3;

const lastCapeIdByRig = new WeakMap<CharacterRig, CapeId>();

interface CapeParticle {
  readonly position: THREE.Vector3;
  readonly previous: THREE.Vector3;
}

class VerletCapeSimulation {
  private readonly particles: CapeParticle[] = [];
  private readonly positionAttribute: THREE.BufferAttribute;
  private readonly rootWorld = new THREE.Vector3();
  private lastRootWorld: THREE.Vector3 | null = null;
  private time = 0;

  constructor(
    private readonly rig: CharacterRig,
    private readonly mesh: THREE.Mesh,
  ) {
    this.positionAttribute = mesh.geometry.getAttribute('position') as THREE.BufferAttribute;
    for (let index = 0; index < this.positionAttribute.count; index += 1) {
      const position = new THREE.Vector3(
        this.positionAttribute.getX(index),
        this.positionAttribute.getY(index),
        this.positionAttribute.getZ(index),
      );
      this.particles.push({
        position,
        previous: position.clone(),
      });
    }
    this.reset();
  }

  update(dt: number): void {
    this.time += dt;
    this.rig.root.getWorldPosition(this.rootWorld);
    if (
      this.lastRootWorld != null &&
      this.rootWorld.distanceTo(this.lastRootWorld) > CAPE_TELEPORT_RESET_DISTANCE
    ) {
      this.reset();
      return;
    }
    this.lastRootWorld = this.rootWorld.clone();
    this.pinAttachmentRows();

    const dtSq = dt * dt;
    const firstDynamicParticle = CAPE_WIDTH_SEGMENTS + 1;
    for (let index = firstDynamicParticle; index < this.particles.length; index += 1) {
      const particle = this.particles[index]!;
      const velocity = particle.position
        .clone()
        .sub(particle.previous)
        .multiplyScalar(CAPE_DAMPING);
      particle.previous.copy(particle.position);
      particle.position.add(velocity);
      particle.position.y -= CAPE_GRAVITY * dtSq;
      const row = Math.floor(index / (CAPE_WIDTH_SEGMENTS + 1));
      const influence = row / CAPE_HEIGHT_SEGMENTS;
      particle.position.x += Math.sin(this.time * 2.4 + row * 0.7) * 0.18 * influence * dtSq;
      particle.position.z -= 0.16 * influence * dtSq;
    }

    for (let iteration = 0; iteration < 4; iteration += 1) {
      this.pinAttachmentRows();
      this.constrainGrid();
      this.applyShoulderSurfaceConstraint();
      this.applyTorsoPushback();
    }
    this.writeGeometry();
  }

  reset(): void {
    this.pinAttachmentRows();
    const top = this.topCenter();
    const backSurfaceZ = this.bodyBackSurfaceZ();
    for (let row = 1; row <= CAPE_HEIGHT_SEGMENTS; row += 1) {
      const v = row / CAPE_HEIGHT_SEGMENTS;
      const rowWidth = capeRowWidth(row);
      const targetZ =
        backSurfaceZ == null
          ? top.z - capeBackOffsetForRow(row)
          : backSurfaceZ - capeSurfaceGapForRow(row);
      for (let col = 0; col <= CAPE_WIDTH_SEGMENTS; col += 1) {
        const u = col / CAPE_WIDTH_SEGMENTS;
        const normalizedX = u * 2 - 1;
        const index = row * (CAPE_WIDTH_SEGMENTS + 1) + col;
        const particle = this.particles[index]!;
        particle.position.set(
          top.x + (normalizedX * rowWidth) / 2,
          top.y - CAPE_HEIGHT * v,
          targetZ,
        );
        particle.previous.copy(particle.position);
      }
    }
    this.rig.root.getWorldPosition(this.rootWorld);
    this.lastRootWorld = this.rootWorld.clone();
    this.writeGeometry();
  }

  dispose(): void {
    this.mesh.removeFromParent();
    this.mesh.geometry.dispose();
    const material = this.mesh.material;
    if (Array.isArray(material)) material.forEach((mat) => mat.dispose());
    else material.dispose();
  }

  private pinAttachmentRows(): void {
    const visualRoot = this.rig.visualRoot ?? this.rig.root;
    const socket = this.rig.resolveSocket?.('cape') ?? visualRoot;
    socket.updateWorldMatrix(true, false);
    visualRoot.updateWorldMatrix(true, false);
    this.rig.bodyMesh.updateWorldMatrix(true, false);

    const anchorPosition = new THREE.Vector3();
    socket.getWorldPosition(anchorPosition);
    const anchorLocal = visualRoot.worldToLocal(anchorPosition);
    const backSurfaceZ = this.bodyBackSurfaceZ();
    const usesRiggedSocket = socket instanceof THREE.Bone;
    anchorLocal.y -= CAPE_PIN_DROP;

    const rowWidth = capeRowWidth(0);
    const centerZ = usesRiggedSocket
      ? anchorLocal.z - CAPE_COLLAR_BACK_OFFSET
      : (backSurfaceZ ?? anchorLocal.z - CAPE_COLLAR_BACK_OFFSET);
    for (let col = 0; col <= CAPE_WIDTH_SEGMENTS; col += 1) {
      const u = col / CAPE_WIDTH_SEGMENTS;
      const normalizedX = u * 2 - 1;
      const collarForward = capeCollarForwardOffsetForColumn(normalizedX);
      const particle = this.particles[col]!;
      particle.position.set(
        anchorLocal.x + (normalizedX * rowWidth) / 2,
        anchorLocal.y - capeCollarDropForColumn(normalizedX),
        centerZ + collarForward,
      );
      particle.previous.copy(particle.position);
    }
  }

  private constrainGrid(): void {
    const rowStride = CAPE_WIDTH_SEGMENTS + 1;
    const top = this.topCenter();
    const backSurfaceZ = this.bodyBackSurfaceZ();
    for (let row = 0; row <= CAPE_HEIGHT_SEGMENTS; row += 1) {
      const rowWidth = capeRowWidth(row);
      const horizontalRest = rowWidth / CAPE_WIDTH_SEGMENTS;
      for (let col = 0; col < CAPE_WIDTH_SEGMENTS; col += 1) {
        this.satisfy(row * rowStride + col, row * rowStride + col + 1, horizontalRest);
      }
    }
    for (let row = 0; row < CAPE_HEIGHT_SEGMENTS; row += 1) {
      for (let col = 0; col <= CAPE_WIDTH_SEGMENTS; col += 1) {
        const verticalRest = capeVerticalRestDistance(row, col, top, backSurfaceZ);
        this.satisfy(row * rowStride + col, (row + 1) * rowStride + col, verticalRest);
      }
    }
  }

  private satisfy(aIndex: number, bIndex: number, restDistance: number): void {
    const a = this.particles[aIndex]!;
    const b = this.particles[bIndex]!;
    const delta = b.position.clone().sub(a.position);
    const distance = delta.length();
    if (distance < 0.0001) return;
    const correction = delta.multiplyScalar((distance - restDistance) / distance);
    if (!this.isPinned(aIndex)) a.position.addScaledVector(correction, 0.5);
    if (!this.isPinned(bIndex)) b.position.addScaledVector(correction, -0.5);
  }

  private applyShoulderSurfaceConstraint(): void {
    const backSurfaceZ = this.bodyBackSurfaceZ();
    if (backSurfaceZ == null) return;
    const rowStride = CAPE_WIDTH_SEGMENTS + 1;
    const targetZ = backSurfaceZ - CAPE_SHOULDER_SURFACE_GAP;
    for (let col = 0; col <= CAPE_WIDTH_SEGMENTS; col += 1) {
      const particle = this.particles[rowStride + col]!;
      particle.position.z = THREE.MathUtils.lerp(
        particle.position.z,
        targetZ,
        CAPE_SHOULDER_SURFACE_STRENGTH,
      );
    }
  }

  private applyTorsoPushback(): void {
    const top = this.topCenter();
    const backSurfaceZ = this.bodyBackSurfaceZ();
    const rowStride = CAPE_WIDTH_SEGMENTS + 1;
    for (let row = 1; row <= CAPE_HEIGHT_SEGMENTS; row += 1) {
      const minZ =
        backSurfaceZ == null
          ? top.z - capeBackOffsetForRow(row)
          : backSurfaceZ - capeSurfaceGapForRow(row);
      for (let col = 0; col <= CAPE_WIDTH_SEGMENTS; col += 1) {
        const particle = this.particles[row * rowStride + col]!;
        if (particle.position.z > minZ) particle.position.z = minZ;
      }
    }
  }

  private topCenter(): THREE.Vector3 {
    return this.particles[Math.floor(CAPE_WIDTH_SEGMENTS / 2)]!.position.clone();
  }

  private isPinned(index: number): boolean {
    return Math.floor(index / (CAPE_WIDTH_SEGMENTS + 1)) === 0;
  }

  private bodyBackSurfaceZ(): number | null {
    const bounds = bodyVisualBounds(this.rig.bodyMesh);
    return bounds == null ? null : bounds.min.z - CAPE_BACK_SURFACE_GAP;
  }

  private writeGeometry(): void {
    for (let index = 0; index < this.particles.length; index += 1) {
      const position = this.particles[index]!.position;
      this.positionAttribute.setXYZ(index, position.x, position.y, position.z);
    }
    this.positionAttribute.needsUpdate = true;
    this.mesh.geometry.computeVertexNormals();
  }
}

function capeRowWidth(row: number): number {
  if (row === 0) return CAPE_COLLAR_WIDTH;
  if (row === 1) return CAPE_SHOULDER_WIDTH;
  const t = (row - 1) / (CAPE_HEIGHT_SEGMENTS - 1);
  return THREE.MathUtils.lerp(CAPE_SHOULDER_WIDTH, CAPE_BOTTOM_WIDTH, t);
}

function capeBackOffsetForRow(row: number): number {
  if (row === 0) return 0;
  if (row === 1) return CAPE_SHOULDER_BACK_DELTA;
  const t = (row - 1) / (CAPE_HEIGHT_SEGMENTS - 1);
  return THREE.MathUtils.lerp(CAPE_SHOULDER_BACK_DELTA, CAPE_BOTTOM_BACK_DELTA, t);
}

function capeSurfaceGapForRow(row: number): number {
  if (row <= 1) return CAPE_SHOULDER_SURFACE_GAP;
  const t = (row - 1) / (CAPE_HEIGHT_SEGMENTS - 1);
  return THREE.MathUtils.lerp(0.035, 0.12, t);
}

function capeCollarForwardOffsetForColumn(normalizedX: number): number {
  return Math.abs(normalizedX) * CAPE_COLLAR_SIDE_FORWARD;
}

function capeCollarDropForColumn(normalizedX: number): number {
  return Math.abs(normalizedX) * CAPE_COLLAR_SIDE_DROP;
}

function capeVerticalRestDistance(
  row: number,
  col: number,
  top: THREE.Vector3,
  backSurfaceZ: number | null,
): number {
  const vertical = CAPE_HEIGHT / CAPE_HEIGHT_SEGMENTS;
  const normalizedX = (col / CAPE_WIDTH_SEGMENTS) * 2 - 1;
  const xDelta = ((capeRowWidth(row + 1) - capeRowWidth(row)) * normalizedX) / 2;
  const zDelta =
    capeRestZForRow(row + 1, top, backSurfaceZ) - capeRestZForRow(row, top, backSurfaceZ);
  return Math.hypot(vertical, xDelta, zDelta);
}

function capeRestZForRow(row: number, top: THREE.Vector3, backSurfaceZ: number | null): number {
  if (row === 0) return top.z;
  return backSurfaceZ == null
    ? top.z - capeBackOffsetForRow(row)
    : backSurfaceZ - capeSurfaceGapForRow(row);
}

function bodyVisualBounds(bodyMesh: THREE.Mesh): THREE.Box3 | null {
  bodyMesh.geometry.computeBoundingBox();
  const geometryBounds = bodyMesh.geometry.boundingBox;
  if (geometryBounds == null) return null;
  if (!Number.isFinite(geometryBounds.min.x) || !Number.isFinite(geometryBounds.max.x)) {
    return null;
  }
  // Sockets and authored body vertices share visual rig units; SkinnedMesh.matrixWorld may
  // include a GLB bind scale that would collapse the body surface toward the skeleton center.
  return geometryBounds.clone();
}

function createCapeGeometry(): THREE.BufferGeometry {
  const positions: number[] = [];
  const uvs: number[] = [];
  const indices: number[] = [];
  const rowStride = CAPE_WIDTH_SEGMENTS + 1;

  for (let row = 0; row <= CAPE_HEIGHT_SEGMENTS; row += 1) {
    const v = row / CAPE_HEIGHT_SEGMENTS;
    const rowWidth = capeRowWidth(row);
    for (let col = 0; col <= CAPE_WIDTH_SEGMENTS; col += 1) {
      const u = col / CAPE_WIDTH_SEGMENTS;
      const normalizedX = u * 2 - 1;
      const collarForward = row === 0 ? capeCollarForwardOffsetForColumn(normalizedX) : 0;
      positions.push(
        normalizedX * rowWidth * 0.5,
        -CAPE_HEIGHT * v - (row === 0 ? capeCollarDropForColumn(normalizedX) : 0),
        -capeBackOffsetForRow(row) + collarForward,
      );
      uvs.push(u, 1 - v);
    }
  }

  for (let row = 0; row < CAPE_HEIGHT_SEGMENTS; row += 1) {
    for (let col = 0; col < CAPE_WIDTH_SEGMENTS; col += 1) {
      const topLeft = row * rowStride + col;
      const topRight = topLeft + 1;
      const bottomLeft = topLeft + rowStride;
      const bottomRight = bottomLeft + 1;
      indices.push(topLeft, bottomLeft, topRight, topRight, bottomLeft, bottomRight);
    }
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setIndex(indices);
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
  geometry.computeVertexNormals();
  return geometry;
}

export function attachCape(args: {
  rig: CharacterRig;
  capeId: CapeId;
  capeTexture: THREE.Texture | null;
}): void {
  const { rig, capeId, capeTexture } = args;

  if (capeId === 'none') {
    if (capeTexture !== null) {
      throw new Error('[attachCape] capeId === "none" requires capeTexture === null.');
    }
    disposeCape(rig);
    lastCapeIdByRig.delete(rig);
    return;
  }

  if (capeTexture === null) {
    throw new Error(`[attachCape] capeTexture is required for non-none capeId "${capeId}".`);
  }

  const priorCapeId = lastCapeIdByRig.get(rig);
  if (priorCapeId === capeId && rig._capeMesh != null) {
    const mat = rig._capeMesh.material as THREE.ShaderMaterial;
    mat.uniforms.uTexture.value = capeTexture;
    return;
  }

  disposeCape(rig);

  const geometry = createCapeGeometry();
  const material = createCapeShader();
  (material.uniforms as { uTexture: THREE.IUniform<THREE.Texture | null> }).uTexture.value =
    capeTexture;
  const mesh = new THREE.Mesh(geometry, material);
  mesh.name = `dynamic-cape:${capeId}`;
  mesh.frustumCulled = false;
  (rig.visualRoot ?? rig.root).add(mesh);
  rig._capeMesh = mesh;
  rig._capeSimulation = new VerletCapeSimulation(rig, mesh);
  applyCapeParams(mesh, capeId);
  lastCapeIdByRig.set(rig, capeId);
}

export function disposeCape(rig: CharacterRig): void {
  if (rig._capeSimulation != null) {
    rig._capeSimulation.dispose();
    rig._capeSimulation = null;
    rig._capeMesh = null;
    return;
  }
  if (rig._capeMesh == null) return;
  rig._capeMesh.removeFromParent();
  rig._capeMesh.geometry.dispose();
  const material = rig._capeMesh.material;
  if (Array.isArray(material)) material.forEach((mat) => mat.dispose());
  else material.dispose();
  rig._capeMesh = null;
}
